import React, { useState } from 'react';
import { adminFetch } from '../../lib/api';
import { Bot, Sparkles, Target, FileText, Mail, MailPlus, BarChart3, AlertTriangle, Calendar, Loader2 } from 'lucide-react';

const FUNCTIONS = [
  { key: 'summary', label: 'Lead-Zusammenfassung', desc: 'KI-Zusammenfassung eines Leads inkl. Aktivitäten', icon: <FileText size={18} />, needsEntity: true, entityType: 'lead' },
  { key: 'priority_suggestion', label: 'Prioritäts-Ranking', desc: 'KI priorisiert alle offenen Leads', icon: <Target size={18} />, needsEntity: false, entityType: 'pipeline' },
  { key: 'next_step', label: 'Nächster Schritt', desc: 'KI-Empfehlung für den optimalen nächsten Schritt', icon: <Sparkles size={18} />, needsEntity: true, entityType: 'lead' },
  { key: 'outreach_draft', label: 'Erstansprache-Entwurf', desc: 'KI erstellt personalisierte Erstansprache-E-Mail', icon: <Mail size={18} />, needsEntity: true, entityType: 'lead' },
  { key: 'follow_up_draft', label: 'Follow-up-Entwurf', desc: 'KI erstellt Follow-up basierend auf Historie', icon: <MailPlus size={18} />, needsEntity: true, entityType: 'lead' },
  { key: 'campaign_review', label: 'Kampagnen-Bewertung', desc: 'KI analysiert Kampagnen-Performance', icon: <BarChart3 size={18} />, needsEntity: true, entityType: 'campaign' },
  { key: 'weekly_briefing', label: 'Wochenbriefing', desc: 'KI erstellt strukturiertes Wochenbriefing', icon: <Calendar size={18} />, needsEntity: false, entityType: 'weekly' },
  { key: 'blocker_detection', label: 'Blocker erkennen', desc: 'KI findet stagnierende Leads', icon: <AlertTriangle size={18} />, needsEntity: false, entityType: 'pipeline' },
];

export const SalesCopilotPage: React.FC = () => {
  const [selectedFn, setSelectedFn] = useState<string | null>(null);
  const [entityId, setEntityId] = useState('');
  const [result, setResult] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [model, setModel] = useState('');
  const [tokens, setTokens] = useState(0);
  const [history, setHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  const runFunction = async () => {
    if (!selectedFn) return;
    const fn = FUNCTIONS.find(f => f.key === selectedFn);
    if (!fn) return;
    if (fn.needsEntity && !entityId) return;

    setLoading(true);
    setResult('');
    try {
      const res = await adminFetch(`/api/admin/sales/copilot/${selectedFn}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityType: fn.entityType,
          entityId: fn.needsEntity ? entityId : undefined,
        }),
      });
      const data = await res.json();
      setResult(data.output || data.error || 'Keine Antwort');
      setModel(data.model || '');
      setTokens(data.tokensUsed || 0);
    } catch (error: any) {
      setResult(`Fehler: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await adminFetch('/api/admin/sales/copilot/briefings?limit=20');
      const data = await res.json();
      setHistory(Array.isArray(data) ? data : []);
      setShowHistory(true);
    } catch (error) {
      console.error(error);
      setHistory([]);
    }
  };

  const currentFn = FUNCTIONS.find(f => f.key === selectedFn);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><Bot style={{ marginRight: 8, verticalAlign: 'middle' }} size={24} /> Sales Copilot</h1>
        <button className="btn btn-ghost" onClick={loadHistory}>📜 Verlauf</button>
      </div>

      {/* Function Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 24 }}>
        {FUNCTIONS.map(fn => (
          <button
            key={fn.key}
            onClick={() => { setSelectedFn(fn.key); setResult(''); setEntityId(''); }}
            className="card"
            style={{
              padding: 16, cursor: 'pointer', textAlign: 'left', border: 'none',
              outline: selectedFn === fn.key ? '2px solid var(--accent-blue)' : 'none',
              transition: 'all 0.2s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <span style={{ color: 'var(--accent-blue)' }}>{fn.icon}</span>
              <strong style={{ fontSize: 14 }}>{fn.label}</strong>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>{fn.desc}</p>
          </button>
        ))}
      </div>

      {/* Input + Run */}
      {selectedFn && (
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12 }}>{currentFn?.icon} {currentFn?.label}</h3>
          {currentFn?.needsEntity && (
            <div style={{ marginBottom: 12 }}>
              <label>{currentFn.entityType === 'campaign' ? 'Kampagnen-ID' : 'Lead-ID'}</label>
              <input
                value={entityId}
                onChange={e => setEntityId(e.target.value)}
                placeholder={`UUID der ${currentFn.entityType === 'campaign' ? 'Kampagne' : 'Fahrschule'} eingeben`}
                style={{ width: '100%', maxWidth: 400 }}
              />
            </div>
          )}
          <button
            className="btn btn-primary"
            onClick={runFunction}
            disabled={loading || (currentFn?.needsEntity === true && !entityId)}
          >
            {loading ? <><Loader2 size={14} className="spin" /> KI analysiert...</> : <><Sparkles size={14} /> Ausführen</>}
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3>🤖 KI-Ergebnis</h3>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-secondary)' }}>
              {model && <span>Modell: {model}</span>}
              {tokens > 0 && <span>{tokens} Tokens</span>}
            </div>
          </div>
          <div style={{
            whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.7,
            padding: 16, background: 'var(--bg-secondary)', borderRadius: 8,
            borderLeft: '3px solid var(--accent-blue)',
          }}>
            {result}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-ghost" onClick={() => navigator.clipboard.writeText(result)}>
              📋 Kopieren
            </button>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>KI-Briefing-Verlauf</h2>
              <button className="btn btn-ghost" onClick={() => setShowHistory(false)}>×</button>
            </div>
            <div style={{ padding: 20 }}>
              {history.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Noch keine Briefings</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {history.map((b: any) => (
                    <div key={b.id} className="card" style={{ padding: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                        <strong>{FUNCTIONS.find(f => f.key === b.briefing_type)?.label || b.briefing_type}</strong>
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {new Date(b.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'hidden' }}>
                        {b.output_text?.substring(0, 300)}...
                      </p>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                        {b.model_used} · {b.tokens_used} Tokens · {b.created_by}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
